@file:Suppress("MagicNumber")

package com.nonopn.skydome.common.app.api.impl

import com.nonopn.skydome.common.app.api.DoNetworkCalls
import com.nonopn.skydome.common.app.api.manageAnswer
import com.nonopn.skydome.common.app.api.GeocodeResult
import com.nonopn.skydome.common.app.api.Mode
import com.nonopn.skydome.common.app.api.model.DashboardImpactReport
import com.nonopn.skydome.common.app.api.model.DashboardImpactReportV2
import com.nonopn.skydome.common.app.detection.ThunderDetection
import com.nonopn.skydome.common.app.pages.keraunic.ApiKeraunicDetail
import com.nonopn.skydome.common.app.pages.keraunic.KeraunicReport
import com.nonopn.skydome.common.app.pages.main.model.api.ApiAnswer
import com.nonopn.skydome.common.app.pages.main.model.api.ApiAnswerProjectWithThunderActivities
import com.nonopn.skydome.common.app.pages.main.model.api.ApiEstimatesData
import com.nonopn.skydome.common.app.pages.main.model.api.Project
import com.nonopn.skydome.common.app.pages.main.model.api.SectionProjectAndThunderDetection
import com.nonopn.skydome.common.app.pages.main.model.api.ThunderDetectionActivity
import com.nonopn.skywatcher.maps.LatLng
import io.ktor.client.HttpClient
import io.ktor.client.request.patch
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.http.encodeURLPathPart
import io.ktor.http.encodeURLQueryComponent
import korlibs.time.DateTime
import korlibs.time.DateTimeTz

@Suppress("TooManyFunctions")
class ApiAdmin(
    private val client: HttpClient
) : ApiInterface {
    private val calls = DoNetworkCalls(client)
    private val prefix = "admin"

    override val mode = Mode.Admin

    override suspend fun probes(from: DateTimeTz, to: DateTimeTz) = probes(from.utc, to.utc)

    override suspend fun probes(from: DateTime, to: DateTime): ApiAnswer = calls.get(
        "https://api.lpsmanager.io/$prefix/probes?" +
                "from=${from.unixMillisLong}&" +
                "to=${to.unixMillisLong}"
    )

    override suspend fun updateProbe(
        probe: ThunderDetection,
        update: UpdateProbe
    ): ThunderDetection {
        // API returns full probe object with extra fields unknown to ThunderDetection.
        // Use postVoid to avoid JSON deserialization failure; return updated copy locally.
        calls.postVoid(
            url = "https://api.lpsmanager.io/$prefix/probe/${probe.uuid}/update",
            body = update
        )
        return probe.copy(name = update.name)
    }

    override suspend fun estimatesData(): List<ApiEstimatesData> =
        calls.get("https://api.lpsmanager.io/$prefix/estimates/data")

    override suspend fun ng(coordinates: LatLng): NgComputation = calls.get(
        "https://api.lpsmanager.io/$prefix/ng?" +
                "latitude=${coordinates.latitude}&" +
                "longitude=${coordinates.longitude}"
    )

    override suspend fun geocoding(address: String): List<GeocodeResult> = calls.get(
        "https://api.lpsmanager.io/$prefix/geocoding?" +
                "address=${address.encodeURLQueryComponent()}"
    )

    override suspend fun certificateDensityActivities(
        from: DateTimeTz,
        to: DateTimeTz
    ) = certificateDensityActivities(from.utc, to.utc)

    override suspend fun certificateDensityActivities(
        from: DateTime,
        to: DateTime
    ): List<SectionProjectAndThunderDetection> = calls.get(
        "https://api.lpsmanager.io/$prefix/probes/densities?" +
                "from=${from.unixMillisLong}&" +
                "to=${to.unixMillisLong}"
    )

    override suspend fun certificateActivities(
        from: DateTimeTz,
        to: DateTimeTz
    ) = certificateActivities(from.utc, to.utc)

    override suspend fun certificateActivities(
        from: DateTime,
        to: DateTime
    ): List<ApiAnswerProjectWithThunderActivities> = calls.get(
        "https://api.lpsmanager.io/$prefix/probes/incidences?" +
                "from=${from.unixMillisLong}&" +
                "to=${to.unixMillisLong}"
    )

    override suspend fun downloadKeraunicReport(
        project: Project,
        from: DateTimeTz,
        to: DateTimeTz
    ) = calls.getBytes(
        (
                "https://api.lpsmanager.io/$prefix/keraunic?lang=en&" +
                        "project=${project.uuid}&" +
                        "fromYear=${from.yearInt}&" +
                        "fromMonth1=${from.month1}&" +
                        "fromDay=${from.dayOfMonth}&" +
                        "toYear=${to.yearInt}&" +
                        "toMonth1=${to.month1}&" +
                        "toDay=${to.dayOfMonth}"
                ).also { println("url -> $it") }
    )

    override suspend fun downloadKeraunicReport(
        coordinates: LatLng,
        from: DateTimeTz,
        to: DateTimeTz
    ) = calls.getBytes(
        (
                "https://api.lpsmanager.io/$prefix/keraunic?lang=en&" +
                        "latitude=${coordinates.latitude}&" +
                        "longitude=${coordinates.longitude}&" +
                        "fromYear=${from.yearInt}&" +
                        "fromMonth1=${from.month1}&" +
                        "fromDay=${from.dayOfMonth}&" +
                        "toYear=${to.yearInt}&" +
                        "toMonth1=${to.month1}&" +
                        "toDay=${to.dayOfMonth}"
                ).also { println("url -> $it") }
    )

    override suspend fun downloadIncidenceReport(
        project: Project,
        instance: ThunderDetectionActivity
    ) = calls.getBytes(
        "https://api.lpsmanager.io/$prefix/incidence?lang=en&" +
                "id=${instance.externalId}&" +
                "project=${project.uuid}"
    )

    override suspend fun downloadIncidenceReport(
        coordinates: LatLng,
        instance: ThunderDetectionActivity
    ) = calls.getBytes(
        "https://api.lpsmanager.io/$prefix/incidence?lang=en&" +
                "id=${instance.externalId}&" +
                "latitude=${coordinates.latitude}&" +
                "longitude=${coordinates.longitude}"
    )

    override suspend fun estimates(): List<ThunderDetectionEstimate> =
        calls.get("https://api.lpsmanager.io/$prefix/estimates")

    /** GET /api/admin/estimates: every estimate with its state, anomalies, payment type and probes. */
    suspend fun adminEstimates(): List<AdminEstimate> =
        calls.get("https://api.lpsmanager.io/api/admin/estimates")

    /**
     * PATCH /api/admin/estimates/{uuid}: changes only what is sent (activation of an External estimate).
     * Answers the saved estimate, as in the list.
     */
    suspend fun patchAdminEstimate(uuid: String, patch: AdminEstimatePatch): AdminEstimate = manageAnswer {
        client.patch("https://api.lpsmanager.io/api/admin/estimates/$uuid") {
            contentType(ContentType.Application.Json)
            setBody(patch)
        }
    }

    override suspend fun createEstimate(request: EstimateRequest): ThunderDetectionEstimate =
        calls.post("https://api.lpsmanager.io/$prefix/estimate", request)

    /** GET /api/admin/promotion-codes: the codes of every app (or of [app]), the deleted ones excluded. */
    suspend fun promotionCodes(app: String? = null): List<AdminPromotionCode> = calls.get(
        "https://api.lpsmanager.io/api/admin/promotion-codes" +
            (app?.let { "?app=${it.encodeURLQueryComponent()}" } ?: "")
    )

    /**
     * POST /api/admin/promotion-codes: creates (uuid null) or updates (uuid set) a code. The body is always the
     * complete code (see [AdminPromotionCodeRequest]). Answers the saved code.
     */
    suspend fun savePromotionCode(request: AdminPromotionCodeRequest): AdminPromotionCode =
        calls.post("https://api.lpsmanager.io/api/admin/promotion-codes", request)

    /** DELETE /api/admin/promotion-codes/{uuid}: irreversible. Answers the deleted code. */
    suspend fun deletePromotionCode(uuid: String): AdminPromotionCode =
        calls.delete("https://api.lpsmanager.io/api/admin/promotion-codes/${uuid.encodeURLPathPart()}")

    // Payments are never handled by the administration: accounts pay through /api/checkout (ApiNormal).
    override suspend fun estimateCheckout(estimateUuid: String): EstimateCheckoutAnswer =
        throw unsupportedPayment()

    override suspend fun billingPortal(app: String): BillingPortalAnswer = throw unsupportedPayment()

    override suspend fun checkoutProbes(request: ProbesCheckoutRequest): ProbesCheckoutAnswer =
        throw unsupportedPayment()

    override suspend fun checkoutAccount(request: AccountCheckoutRequest): ProbesCheckoutAnswer =
        throw unsupportedPayment()

    override suspend fun checkoutUpgrade(request: AccountCheckoutRequest): ProbesCheckoutAnswer =
        throw unsupportedPayment()

    private fun unsupportedPayment() =
        UnsupportedOperationException("Payment is not available in administrator mode")

    override suspend fun accountStatus(app: String): AccountStatus =
        calls.get("https://api.lpsmanager.io/api/account/status?app=$app")

    override suspend fun keraunicDetail(
        coordinates: LatLng,
        from: DateTimeTz,
        to: DateTimeTz,
    ): ApiKeraunicDetail = calls.get(
        "https://api.lpsmanager.io/skydome/report/keraunic?" +
                "latitude=${coordinates.latitude}&" +
                "longitude=${coordinates.longitude}&" +
                "fromSeconds=${from.utc.unixMillisLong / 1000}&" +
                "toSeconds=${to.utc.unixMillisLong / 1000}"
    )

    override suspend fun keraunicReport(
        coordinates: LatLng,
        from: DateTimeTz,
        to: DateTimeTz,
    ): KeraunicReport =
        calls.get(
            "https://api.lpsmanager.io/$prefix/keraunic?" +
                    "latitude=${coordinates.latitude}&" +
                    "longitude=${coordinates.longitude}&" +
                    "from=${from.utc.unixMillisLong / 1000}&" +
                    "to=${to.utc.unixMillisLong / 1000}"
        )

    override suspend fun dashboardProbeReport(): List<DashboardImpactReport> =
        calls.get("https://api.lpsmanager.io/$prefix/dashboard/report")

    override suspend fun dashboardProbeReportV2(): List<DashboardImpactReportV2> =
        calls.get(
            "https://api.lpsmanager.io/$prefix/dashboard?" +
                    "deltaSeconds=${DateTimeTz.nowLocal().offset.totalMilliseconds / 1000}}"
        )
}
